import React, {
  FC, ReactNode, useCallback, useMemo,
} from 'react';
import { selectUserById } from 'models/twilio';
import styled from 'styled-components';
import { BEHAVIOR, CUSTOM_USERS } from 'components/twilio/constants';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import { useSelector } from 'react-redux';
import UserAvatar from '../styled/UserAvatar';
import { ChatTitleSubtitleGroup } from '../CommonChatDisplayComponents';

const SenderWrapperContainer = styled.div`
  display: flex;
  align-items: flex-end;
  margin: 8px 0;
`;

const StyledChatTitleSubtitleGroup = styled(ChatTitleSubtitleGroup)<{ $clickable: boolean }>`
  margin-left: 4px;
  max-width: ${BEHAVIOR.CHAT_GROUP_MAX_WIDTH};
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
`;

const AvatarContainer = styled.div<{ $clickable: boolean }>`
  margin: 0 10px 2px 0;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
  width: 100%;
`;

interface SenderWrapperProps {
  author: string;
  children: ReactNode;
}

const SenderWrapper: FC<SenderWrapperProps> = ({ author, children }: SenderWrapperProps) => {
  const user = useSelector(selectUserById(author));
  const { createPrivateChat } = useTwilioEventFunctions();

  const isCustomUser = useMemo(() => CUSTOM_USERS.includes(author), [author]);

  const onUserClick = useCallback(async () => {
    if (isCustomUser) return;
    await createPrivateChat(author);
  }, [createPrivateChat, author, isCustomUser]);

  const UserInformation = useMemo(() => (
    <StyledChatTitleSubtitleGroup
      $clickable={!isCustomUser}
      onClick={onUserClick}
      title={user?.name || author}
      tag={user?.tag}
      subtitle={user?.description}
    />
  ), [user, author, onUserClick, isCustomUser]);

  return (
    <SenderWrapperContainer>
      <AvatarContainer $clickable={!isCustomUser} onClick={onUserClick}>
        <UserAvatar name={user?.name || author} />
      </AvatarContainer>
      <MessageWrapper>
        {UserInformation}
        {children}
      </MessageWrapper>
    </SenderWrapperContainer>
  );
};

export default React.memo(SenderWrapper);
